import { isSymbolBytes } from './model';
import { canvasPng, symbolCanvas, withSymbolImage } from './images';
import {
  SymbolImportError,
  SYMBOL_LIMITS,
  symbolHash,
  symbolName,
  type SymbolAsset,
  type SymbolMimeType,
} from './model';
import { inspectPng, pngSignature } from './png';
import { sanitizeSymbolSvg } from './svg';

export interface ImportedSymbol {
  readonly asset: SymbolAsset;
  readonly notice?: string;
}
export async function importSymbolFile(file: File, signal?: AbortSignal): Promise<ImportedSymbol> {
  if (!file.size || file.size > SYMBOL_LIMITS.pngBytes)
    throw new SymbolImportError(
      'This symbol is empty or too large. Use PNG up to 5 MiB or SVG up to 512 KiB.',
    );
  if (
    file.type &&
    !['image/png', 'image/svg+xml', 'application/octet-stream'].includes(file.type.toLowerCase())
  )
    throw new SymbolImportError('This file is not a supported PNG or SVG.');
  signal?.throwIfAborted();
  let bytes: Uint8Array = new Uint8Array(await file.arrayBuffer());
  signal?.throwIfAborted();
  let mimeType: SymbolMimeType, width: number, height: number, notice: string | undefined;
  if (pngSignature(bytes)) {
    mimeType = 'image/png';
    const dimensions = inspectPng(bytes);
    ({ width, height } = dimensions);
    const scale = Math.min(1, SYMBOL_LIMITS.retainedDimension / Math.max(width, height));
    const retainedWidth = Math.max(1, Math.round(width * scale)),
      retainedHeight = Math.max(1, Math.round(height * scale));
    bytes = await withSymbolImage(
      bytes,
      mimeType,
      async (image) => {
        if (image.naturalWidth !== width || image.naturalHeight !== height)
          throw new SymbolImportError('The decoded PNG dimensions do not match its header.');
        const { canvas, ctx } = symbolCanvas(retainedWidth, retainedHeight);
        try {
          ctx.drawImage(image, 0, 0, retainedWidth, retainedHeight);
          return await canvasPng(canvas);
        } finally {
          canvas.width = 0;
          canvas.height = 0;
        }
      },
      signal,
    );
    width = retainedWidth;
    height = retainedHeight;
    inspectPng(bytes);
    if (scale < 1) notice = 'The PNG was proportionally reduced to a 2,048px long edge.';
  } else {
    mimeType = 'image/svg+xml';
    ({ bytes, width, height } = sanitizeSymbolSvg(bytes));
  }
  if (file.type.startsWith('image/') && file.type !== mimeType)
    throw new SymbolImportError('This image content does not match its file type.');
  const sha256 = await symbolHash(bytes);
  signal?.throwIfAborted();
  const name = symbolName(file.name.replace(/\.[^.]+$/, ''));
  return {
    asset: { id: `sym_${crypto.randomUUID()}`, name, mimeType, bytes, sha256, width, height },
    ...(notice ? { notice } : {}),
  };
}

/** Project resources pass the same security boundary before any rendering or replacement. */
export async function validateSymbolAsset(asset: SymbolAsset): Promise<void> {
  if ((await symbolHash(asset.bytes)) !== asset.sha256)
    throw new SymbolImportError('A symbol resource has a mismatched content hash.');
  const checked =
    asset.mimeType === 'image/png' ? inspectPng(asset.bytes) : sanitizeSymbolSvg(asset.bytes);
  if (checked.width !== asset.width || checked.height !== asset.height)
    throw new SymbolImportError('A symbol resource has incorrect dimensions.');
  if (
    'bytes' in checked &&
    isSymbolBytes(checked.bytes) &&
    (checked.bytes.length !== asset.bytes.length ||
      checked.bytes.some((n: number, i: number) => n !== asset.bytes[i]))
  )
    throw new SymbolImportError(
      'A stored SVG does not match the current safe static-vector policy.',
    );
  if (
    asset.mimeType === 'image/png' &&
    Math.max(asset.width, asset.height) > SYMBOL_LIMITS.retainedDimension
  )
    throw new SymbolImportError('A stored PNG exceeds the retained symbol dimension limit.');
}
